import { useState, useEffect, useRef, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { vendorApi } from '../../services/vendor';
import api from '../../services/api';
import extractApiError from '../../utils/extractApiError';
import type { PlatformInvite } from '../../types';

const inputClass =
  'w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const INDUSTRY_OPTIONS = [
  'Security',
  'Property Management',
  'Facilities Management',
  'Healthcare',
  'Education',
  'Government',
  'Other',
];

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'];

export default function VendorOrganization() {
  const queryClient = useQueryClient();

  const { data: org, isLoading } = useQuery({
    queryKey: ['vendor', 'organization'],
    queryFn: () => vendorApi.getOrganization().then((r) => r.data),
  });

  // --- Organization info form ---
  const [orgForm, setOrgForm] = useState({
    name: '',
    legal_name: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    industry: '',
    company_size: '',
    description: '',
  });
  const [orgSaveSuccess, setOrgSaveSuccess] = useState(false);
  const [orgSaveError, setOrgSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (org) {
      setOrgForm({
        name: org.name || '',
        legal_name: org.legal_name || '',
        address: org.address || '',
        address_line_2: org.address_line_2 || '',
        city: org.city || '',
        state: org.state || '',
        zip_code: org.zip_code || '',
        phone: org.phone || '',
        website: org.website || '',
        industry: org.industry || '',
        company_size: org.company_size || '',
        description: org.description || '',
      });
    }
  }, [org]);

  const updateOrgMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => vendorApi.updateOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', 'organization'] });
      setOrgSaveError(null);
      setOrgSaveSuccess(true);
      setTimeout(() => setOrgSaveSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      setOrgSaveSuccess(false);
      setOrgSaveError(extractApiError(err));
    },
  });

  const handleOrgSubmit = (e: FormEvent) => {
    e.preventDefault();
    updateOrgMutation.mutate(orgForm);
  };

  // --- Logo upload ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoSuccess, setLogoSuccess] = useState(false);

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Please select a valid image file (PNG, JPG, GIF, WebP, or SVG).');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Image must be smaller than 2MB.');
      return;
    }

    setLogoUploading(true);
    setLogoError(null);
    setLogoSuccess(false);

    try {
      const formData = new FormData();
      formData.append('logo', file);
      await api.patch('/vendor/organization/', formData);
      queryClient.invalidateQueries({ queryKey: ['vendor', 'organization'] });
      setLogoSuccess(true);
      setTimeout(() => setLogoSuccess(false), 3000);
    } catch (err: unknown) {
      setLogoError(extractApiError(err));
    } finally {
      setLogoUploading(false);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const orgInitials = org
    ? org.name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '';

  // --- Email whitelist ---
  const [whitelistEmails, setWhitelistEmails] = useState<string[]>([]);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState('');
  const [whitelistError, setWhitelistError] = useState<string | null>(null);
  const [whitelistSuccess, setWhitelistSuccess] = useState(false);

  useEffect(() => {
    if (org) {
      setWhitelistEmails(org.whitelisted_emails || []);
    }
  }, [org]);

  const updateWhitelistMutation = useMutation({
    mutationFn: (emails: string[]) =>
      vendorApi.updateOrganization({ whitelisted_emails: emails }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', 'organization'] });
      setWhitelistError(null);
      setWhitelistSuccess(true);
      setTimeout(() => setWhitelistSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      setWhitelistSuccess(false);
      setWhitelistError(extractApiError(err));
    },
  });

  const handleAddWhitelistEmail = (e: FormEvent) => {
    e.preventDefault();
    setWhitelistError(null);
    const email = newWhitelistEmail.trim().toLowerCase();
    if (!email) return;

    // Simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setWhitelistError('Please enter a valid email address.');
      return;
    }

    if (whitelistEmails.includes(email)) {
      setWhitelistError('This email is already in the whitelist.');
      return;
    }

    const updatedEmails = [...whitelistEmails, email];
    setWhitelistEmails(updatedEmails);
    setNewWhitelistEmail('');
    updateWhitelistMutation.mutate(updatedEmails);
  };

  const handleRemoveWhitelistEmail = (emailToRemove: string) => {
    const updatedEmails = whitelistEmails.filter((e) => e !== emailToRemove);
    setWhitelistEmails(updatedEmails);
    updateWhitelistMutation.mutate(updatedEmails);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Organization" description="Manage your organization profile and settings" />

      {/* Logo Section */}
      <div className="max-w-2xl mb-8">
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Organization Logo</h2>
          <div className="flex items-center gap-6">
            {org?.logo ? (
              <img
                src={org.logo}
                alt={`${org.name} logo`}
                className="h-20 w-20 rounded-2xl object-cover border-2 border-slate-200"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-100 border-2 border-brand-200">
                <span className="text-2xl font-bold text-brand-600">{orgInitials}</span>
              </div>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                onChange={handleLogoChange}
                className="hidden"
                id="logo_upload"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoUploading}
                className="px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-xl hover:bg-brand-100 disabled:opacity-50"
              >
                {logoUploading ? 'Uploading...' : 'Change Logo'}
              </button>
              <p className="mt-1 text-xs text-slate-400">PNG, JPG, GIF, WebP, or SVG. Max 2MB.</p>
              {logoSuccess && (
                <p className="mt-1 text-xs text-emerald-600 font-medium">Logo updated successfully</p>
              )}
              {logoError && (
                <p className="mt-1 text-xs text-red-600 font-medium">{logoError}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Organization Info Form */}
      <form onSubmit={handleOrgSubmit} className="max-w-2xl mb-8">
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Organization Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="org_name" className="block text-sm font-medium text-slate-700 mb-1">
                Company Name
              </label>
              <input
                id="org_name"
                type="text"
                value={orgForm.name}
                onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="org_legal_name" className="block text-sm font-medium text-slate-700 mb-1">
                Legal Name
              </label>
              <input
                id="org_legal_name"
                type="text"
                value={orgForm.legal_name}
                onChange={(e) => setOrgForm({ ...orgForm, legal_name: e.target.value })}
                className={inputClass}
                placeholder="Full legal entity name"
              />
            </div>
            <div>
              <label htmlFor="org_industry" className="block text-sm font-medium text-slate-700 mb-1">
                Industry
              </label>
              <select
                id="org_industry"
                value={orgForm.industry}
                onChange={(e) => setOrgForm({ ...orgForm, industry: e.target.value })}
                className={inputClass}
              >
                <option value="">Select industry...</option>
                {INDUSTRY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="org_company_size" className="block text-sm font-medium text-slate-700 mb-1">
                Company Size
              </label>
              <select
                id="org_company_size"
                value={orgForm.company_size}
                onChange={(e) => setOrgForm({ ...orgForm, company_size: e.target.value })}
                className={inputClass}
              >
                <option value="">Select size...</option>
                {COMPANY_SIZE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} employees
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="org_description" className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <textarea
                id="org_description"
                value={orgForm.description}
                onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })}
                rows={3}
                className={inputClass}
                placeholder="Brief description of your organization..."
              />
            </div>

            <div className="sm:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Address</h3>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="org_address" className="block text-sm font-medium text-slate-700 mb-1">
                Address Line 1
              </label>
              <input
                id="org_address"
                type="text"
                value={orgForm.address}
                onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="org_address_line_2" className="block text-sm font-medium text-slate-700 mb-1">
                Address Line 2
              </label>
              <input
                id="org_address_line_2"
                type="text"
                value={orgForm.address_line_2}
                onChange={(e) => setOrgForm({ ...orgForm, address_line_2: e.target.value })}
                className={inputClass}
                placeholder="Suite, unit, floor, etc."
              />
            </div>
            <div>
              <label htmlFor="org_city" className="block text-sm font-medium text-slate-700 mb-1">
                City
              </label>
              <input
                id="org_city"
                type="text"
                value={orgForm.city}
                onChange={(e) => setOrgForm({ ...orgForm, city: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="org_state" className="block text-sm font-medium text-slate-700 mb-1">
                State
              </label>
              <input
                id="org_state"
                type="text"
                value={orgForm.state}
                onChange={(e) => setOrgForm({ ...orgForm, state: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="org_zip" className="block text-sm font-medium text-slate-700 mb-1">
                ZIP Code
              </label>
              <input
                id="org_zip"
                type="text"
                value={orgForm.zip_code}
                onChange={(e) => setOrgForm({ ...orgForm, zip_code: e.target.value })}
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Contact</h3>
            </div>
            <div>
              <label htmlFor="org_phone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                id="org_phone"
                type="tel"
                value={orgForm.phone}
                onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="org_website" className="block text-sm font-medium text-slate-700 mb-1">
                Website
              </label>
              <input
                id="org_website"
                type="url"
                value={orgForm.website}
                onChange={(e) => setOrgForm({ ...orgForm, website: e.target.value })}
                className={inputClass}
                placeholder="https://"
              />
            </div>
          </div>
          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={updateOrgMutation.isPending}
              className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
            >
              {updateOrgMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            {orgSaveSuccess && (
              <span className="text-sm text-emerald-600 font-medium">Saved successfully</span>
            )}
            {orgSaveError && (
              <span className="text-sm text-red-600 font-medium">{orgSaveError}</span>
            )}
          </div>
        </div>
      </form>

      {/* Email Whitelist */}
      <div className="max-w-2xl mb-8">
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Email Whitelist</h2>
          <p className="text-sm text-slate-500 mb-4">
            Only emails from whitelisted addresses will be accepted for report ingestion.
          </p>

          {/* Add email form */}
          <form onSubmit={handleAddWhitelistEmail} className="flex gap-3 mb-4">
            <input
              type="email"
              value={newWhitelistEmail}
              onChange={(e) => setNewWhitelistEmail(e.target.value)}
              placeholder="email@example.com"
              className={`flex-1 ${inputClass}`}
            />
            <button
              type="submit"
              disabled={updateWhitelistMutation.isPending || !newWhitelistEmail.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap"
            >
              Add Email
            </button>
          </form>

          {whitelistError && <p className="mb-3 text-sm text-red-600">{whitelistError}</p>}
          {whitelistSuccess && (
            <p className="mb-3 text-sm text-emerald-600 font-medium">Whitelist updated successfully</p>
          )}

          {/* Email list */}
          {whitelistEmails.length > 0 ? (
            <div className="space-y-2">
              {whitelistEmails.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50"
                >
                  <span className="text-sm text-slate-700">{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveWhitelistEmail(email)}
                    disabled={updateWhitelistMutation.isPending}
                    className="text-sm text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No whitelisted emails. Add one above.</p>
          )}
        </div>
      </div>

      {/* Platform Invites */}
      <PlatformInvitesSection />
    </>
  );
}


// ---------------------------------------------------------------------------
// Platform Invites sub-component
// ---------------------------------------------------------------------------

function PlatformInvitesSection() {
  const queryClient = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const { data: invites } = useQuery({
    queryKey: ['platform-invites'],
    queryFn: () => vendorApi.getPlatformInvites().then((r) => r.data),
  });

  const sendInviteMutation = useMutation({
    mutationFn: (email: string) => vendorApi.sendPlatformInvite(email),
    onSuccess: (_data, email) => {
      queryClient.invalidateQueries({ queryKey: ['platform-invites'] });
      setInviteEmail('');
      setInviteError(null);
      setInviteSuccess(`Invitation sent to ${email}`);
      setTimeout(() => setInviteSuccess(null), 4000);
    },
    onError: (err: unknown) => {
      setInviteSuccess(null);
      setInviteError(extractApiError(err));
    },
  });

  const handleSendInvite = (e: FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInviteError('Please enter a valid email address.');
      return;
    }
    sendInviteMutation.mutate(email);
  };

  const statusBadge = (status: PlatformInvite['status']) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700',
      accepted: 'bg-emerald-50 text-emerald-700',
      expired: 'bg-slate-100 text-slate-500',
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="max-w-2xl mb-8">
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Invite to GuardGrade</h2>
        <p className="text-sm text-slate-500 mb-4">
          Invite clients to join GuardGrade. They will receive a signup link with the client role pre-selected.
        </p>

        {/* Send invite form */}
        <form onSubmit={handleSendInvite} className="flex gap-3 mb-4">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="client@example.com"
            className="flex-1 w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={sendInviteMutation.isPending || !inviteEmail.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap"
          >
            {sendInviteMutation.isPending ? 'Sending...' : 'Send Invite'}
          </button>
        </form>

        {inviteError && <p className="mb-3 text-sm text-red-600">{inviteError}</p>}
        {inviteSuccess && <p className="mb-3 text-sm text-emerald-600 font-medium">{inviteSuccess}</p>}

        {/* Invites list */}
        {invites && invites.length > 0 ? (
          <div className="space-y-2">
            {invites.map((invite: PlatformInvite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-3 rounded-xl bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{invite.invited_email}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {new Date(invite.created_at).toLocaleDateString()} &middot; {invite.suggested_role}
                  </p>
                </div>
                {statusBadge(invite.status)}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No platform invites sent yet.</p>
        )}
      </div>
    </div>
  );
}
